import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Features', href: '/features' },
    { label: 'Tour', href: '/tour' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Free Kit', href: '/probate-checklist' },
    { label: 'Live Demo', href: '/login?demo=1' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-soft' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1" data-testid="navbar-logo">
            <span className={`font-heading text-2xl font-bold tracking-tight ${isScrolled ? 'text-ep-primary' : 'text-ep-primary'}`}>
              Estate
            </span>
            <span className="font-heading text-2xl font-bold tracking-tight text-ep-secondary">Pro</span>
            <span className={`font-heading text-2xl font-bold tracking-tight ${isScrolled ? 'text-ep-primary' : 'text-ep-primary'}`}>
              Filer
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-ep-primary'
                    : isScrolled 
                      ? 'text-ep-text-secondary hover:text-ep-primary' 
                      : 'text-ep-text-secondary hover:text-ep-primary'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/login">
              <Button 
                variant="ghost" 
                className="text-ep-text-secondary hover:text-ep-primary hover:bg-ep-subtle"
                data-testid="nav-login-btn"
              >
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="bg-ep-primary text-white hover:bg-ep-primary-dark px-6"
                data-testid="nav-register-btn"
              >
                Get Started Free
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-ep-text"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-ep-border" data-testid="mobile-menu">
          <div className="container-custom py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`block text-base font-medium py-2 ${
                  isActive(link.href) ? 'text-ep-primary' : 'text-ep-text-secondary'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-ep-border space-y-3">
              <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full">Sign In</Button>
              </Link>
              <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full bg-ep-primary text-white hover:bg-ep-primary-dark">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
